import { createContext, useContext, useMemo, ReactNode } from 'react';

import { useAuth } from './AuthProvider';

interface Tenant {
  id: string;
  name: string;
  slug?: string;
}

interface TenantContextValue {
  tenant: Tenant | null;
  tenantId: string | null;
  setTenant: (tenant: Tenant | null) => void;
  isLoading: boolean;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

export function TenantProvider({ children }: { children: ReactNode }) {
  const { currentOrganization, isLoading: authLoading, switchOrganization } = useAuth();

  const tenant: Tenant | null = useMemo(() => {
    if (!currentOrganization?.organization) return null;
    return {
      id: currentOrganization.organization_id,
      name: currentOrganization.organization?.name || '',
      slug: currentOrganization.organization?.slug,
    };
  }, [currentOrganization]);

  const setTenant = (newTenant: Tenant | null) => {
    if (newTenant) {
      switchOrganization(newTenant.id);
    }
  };

  const value: TenantContextValue = {
    tenant,
    tenantId: tenant?.id || null,
    setTenant,
    isLoading: authLoading,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
